import { Fragment, useEffect, useState } from 'react';
import { Icon, Label, Table } from 'semantic-ui-react';
import {
  BuyerProfileImportChange,
  BuyerProfileImportRun,
  BuyerProfileImportSummary,
  listBuyerProfileImports,
} from '~/api/admin';

const TYPE_COLORS: Record<BuyerProfileImportChange['type'], 'green' | 'blue' | 'red'> = {
  CREATE: 'green',
  EDIT: 'blue',
  DELETE: 'red',
};

const STATUS_COLORS: Record<string, 'green' | 'red' | 'grey' | 'yellow'> = {
  COMPLETED: 'green',
  ERROR: 'red',
  PROCESSING: 'yellow',
  PENDING: 'yellow',
};

/** Render a cell value; null/blank shows as an em dash, objects as JSON. */
const fmtValue = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

/**
 * Company name linked to its profile page (new tab). Falls back to the hashid
 * when the name couldn't be resolved. stopPropagation keeps the click from
 * toggling the surrounding collapsible row.
 */
const CompanyLink = ({ company, name }: { company: string; name?: string }) => (
  <a
    href={`/company-profile/${company}`}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e) => e.stopPropagation()}
  >
    {name ?? company}
    <Icon name="external" size="small" style={{ marginLeft: '0.25rem', position: 'relative', top: '-2px' }} />
  </a>
);

const groupByCompany = (
  changes: BuyerProfileImportChange[]
): [string, BuyerProfileImportChange[]][] => {
  const byCompany = new Map<string, BuyerProfileImportChange[]>();
  for (const change of changes) {
    const list = byCompany.get(change.company) ?? [];
    list.push(change);
    byCompany.set(change.company, list);
  }
  return Array.from(byCompany.entries());
};

interface Props {
  /** Bumped whenever an import starts/completes so the list refetches. */
  refreshKey?: number;
}

/**
 * Audit view of every buyer-profile import run (newest first), loaded from the
 * server. Each run expands to its per-data-point change log (grouped per
 * company) and the rows it skipped, so a run that changed nothing still shows
 * what it processed and why.
 */
export default function BuyerProfileImportChanges({ refreshKey = 0 }: Props) {
  const [runs, setRuns] = useState<BuyerProfileImportRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listBuyerProfileImports()
      .then((data) => {
        if (cancelled) return;
        setRuns(data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError('Could not load past imports.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div style={{ width: '100%', marginTop: '0.75rem' }}>
      <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
        Import history
      </div>

      {error && <div style={{ color: '#9f3a38' }}>{error}</div>}
      {!error && loading && runs.length === 0 && (
        <div style={{ color: '#767676' }}>Loading…</div>
      )}
      {!error && !loading && runs.length === 0 && (
        <div style={{ color: '#767676' }}>No imports yet.</div>
      )}

      {runs.map((run) => {
        const runKey = `run:${run.id}`;
        const isRunOpen = expanded.has(runKey);
        const summary: BuyerProfileImportSummary = run.summary;
        const changes = summary.changes ?? [];
        const errors = summary.errors ?? [];
        const groups = groupByCompany(changes);

        return (
          <div
            key={run.id}
            style={{ border: '1px solid #e0e1e2', borderRadius: 4, marginBottom: '0.5rem' }}
          >
            <div
              onClick={() => toggle(runKey)}
              style={{
                cursor: 'pointer',
                padding: '0.5rem 0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flexWrap: 'wrap',
              }}
            >
              <Icon
                name={isRunOpen ? 'chevron down' : 'chevron right'}
                style={{ position: 'relative', top: '-4px' }}
              />
              <span style={{ fontWeight: 600 }}>{fmtDate(run.createdAt)}</span>
              <Label size="tiny" color={STATUS_COLORS[run.status] ?? 'grey'}>
                {run.status}
              </Label>
              <span style={{ color: '#767676', marginLeft: 'auto' }}>
                {summary.profilesProcessed} processed · {summary.profilesChanged} changed
                {' · '}
                {changes.length} data point{changes.length === 1 ? '' : 's'}
                {summary.profilesSkipped ? ` · ${summary.profilesSkipped} blank` : ''}
                {errors.length > 0 ? ` · ${errors.length} skipped` : ''}
              </span>
            </div>

            {isRunOpen && (
              <div style={{ padding: '0 0.75rem 0.75rem' }}>
                {run.fileName && (
                  <div style={{ color: '#767676', marginBottom: '0.5rem' }}>
                    File: {run.fileName}
                  </div>
                )}

                {run.errorMessage && (
                  <div style={{ color: '#9f3a38', marginBottom: '0.5rem' }}>
                    {run.errorMessage}
                  </div>
                )}

                <div style={{ color: '#767676', marginBottom: '0.5rem' }}>
                  {summary.answersCreated} added, {summary.answersUpdated} updated,{' '}
                  {summary.answersDeleted} cleared
                </div>

                {!!summary.profilesSkipped && (
                  <div style={{ color: '#767676', marginBottom: '0.5rem' }}>
                    {summary.profilesSkipped} blank row{summary.profilesSkipped === 1 ? '' : 's'}{' '}
                    skipped — the export includes a template row for every eligible
                    company, and rows left blank for companies without a profile are
                    left untouched.
                  </div>
                )}

                {changes.length === 0 ? (
                  <div style={{ color: '#767676' }}>No data points were changed.</div>
                ) : (
                  <Table compact size="small" style={{ width: '100%' }}>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Company / Question</Table.HeaderCell>
                        <Table.HeaderCell>Segment</Table.HeaderCell>
                        <Table.HeaderCell>Data point</Table.HeaderCell>
                        <Table.HeaderCell>Change</Table.HeaderCell>
                        <Table.HeaderCell>Type</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {groups.map(([company, rows]) => {
                        const coKey = `co:${run.id}:${company}`;
                        const isOpen = expanded.has(coKey);
                        return (
                          <Fragment key={coKey}>
                            <Table.Row
                              onClick={() => toggle(coKey)}
                              style={{ cursor: 'pointer', background: '#f9fafb' }}
                            >
                              <Table.Cell style={{ fontWeight: 600 }} colSpan={5}>
                                <Icon name={isOpen ? 'chevron down' : 'chevron right'} />
                                <CompanyLink company={company} name={run.companyNames?.[company]} />
                                <span style={{ marginLeft: '0.5rem', color: '#767676', fontWeight: 400 }}>
                                  ({rows.length} change{rows.length === 1 ? '' : 's'})
                                </span>
                              </Table.Cell>
                            </Table.Row>
                            {isOpen &&
                              rows.map((c, i) => (
                                <Table.Row key={`${coKey}-${i}`}>
                                  <Table.Cell style={{ paddingLeft: '2.5rem' }}>
                                    {c.questionKey}
                                  </Table.Cell>
                                  <Table.Cell>{c.segment ?? '—'}</Table.Cell>
                                  <Table.Cell>{c.dataPoint}</Table.Cell>
                                  <Table.Cell>
                                    {fmtValue(c.from)} → {fmtValue(c.to)}
                                  </Table.Cell>
                                  <Table.Cell>
                                    <Label size="tiny" color={TYPE_COLORS[c.type]}>
                                      {c.type}
                                    </Label>
                                  </Table.Cell>
                                </Table.Row>
                              ))}
                          </Fragment>
                        );
                      })}
                    </Table.Body>
                  </Table>
                )}

                {errors.length > 0 && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#9f3a38' }}>
                      Skipped rows ({errors.length})
                    </div>
                    <Table compact size="small" style={{ width: '100%' }}>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell width={1}>Row</Table.HeaderCell>
                          <Table.HeaderCell width={3}>Company</Table.HeaderCell>
                          <Table.HeaderCell>Reason</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {errors.map((e, i) => (
                          <Table.Row key={`${run.id}-err-${i}`}>
                            <Table.Cell>{e.row}</Table.Cell>
                            <Table.Cell>{e.companyId ?? '—'}</Table.Cell>
                            <Table.Cell>{e.message}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
